import { KeyboardEvent, useRef, useState } from 'react';
import { POPOVER_Z_CLASS } from '@constants/z-index';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';

import { Props } from './tooltip.type';

export const Tooltip = ({
  button,
  popoverContent,
  ariaLabel,
  ariaDescribedBy,
  buttonStyles,
  popoverClasses = 'relative',
  buttonClasses = '',
  popoverStyles,
  buttonTestId = '',
  buttonRef,
  anchorProps = { to: 'bottom start', gap: 16 },
  activateWithHover = false,
  onButtonClick,
}: Props & { activateWithHover?: boolean }) => {
  const [hoverOpen, setHoverOpen] = useState(false);
  // The render prop owns `close`, but the mouse handlers live on `Popover`, one level above it.
  // Without this, leaving the trigger cleared `hoverOpen` and left the Popover's own `open` set, so
  // a hover-then-click latched the panel open until a second click.
  const closeRef = useRef<(() => void) | null>(null);

  return (
    <Popover
      className={popoverClasses}
      style={popoverStyles}
      onMouseEnter={activateWithHover ? () => setHoverOpen(true) : undefined}
      onMouseLeave={
        activateWithHover
          ? () => {
              setHoverOpen(false);
              closeRef.current?.();
            }
          : undefined
      }>
      {({ open, close }) => {
        // Only while the Popover is genuinely open. That `close` is Headless UI's
        // `refocusableClose`, which calls `.focus()` on the trigger whether or not the panel was
        // showing, so holding it unconditionally moved focus on every `mouseleave` — including a
        // pointer that swept past an info icon while the user was typing in the field beside it.
        // Hover alone never sets `open`, and the click that does already left focus on the trigger,
        // so the call that survives moves nothing.
        closeRef.current = open ? close : null;

        // `onButtonClick` belongs to the press that opens the panel; the one that closes it only
        // dismisses. `PopoverButton` opens on Enter and Space from its own keydown and cancels the
        // click they would fire, so the keydown runs it too. Both handlers run before Headless UI's,
        // while `open` is still the state the press acts on.
        const handleOpeningPress = () => {
          if (!open) onButtonClick?.();
        };

        // A held key is one press. Headless UI toggles on every repeat, so the panel would flicker and
        // copy again on each reopening; a prevented keydown never reaches its handler.
        const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          if (event.repeat) {
            event.preventDefault();
            return;
          }
          handleOpeningPress();
        };

        return (
          <>
            <PopoverButton
              ref={buttonRef}
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              style={buttonStyles}
              className={buttonClasses}
              data-testid={buttonTestId}
              onClick={handleOpeningPress}
              onKeyDown={handleKeyDown}>
              {button}
            </PopoverButton>
            <Transition
              // `hoverOpen || open`, not either/or: a hover-only panel is unreachable on touch, where
              // there is no hover, and by keyboard, where `PopoverButton` opens on Enter. Keeping the
              // Popover's own `open` in the condition leaves hover as an extra affordance on desktop
              // rather than the only one.
              show={activateWithHover ? hoverOpen || open : open}
              enter='transition duration-200 ease-out'
              enterFrom='scale-95 opacity-0'
              enterTo='scale-100 opacity-100'
              leave=' transition duration-175 ease-out'
              leaveFrom='scale-100 opacity-100'
              leaveTo='scale-95 opacity-0'>
              <PopoverPanel anchor={anchorProps} className={`${POPOVER_Z_CLASS} flex origin-top flex-col transition`}>
                {popoverContent}
              </PopoverPanel>
            </Transition>
          </>
        );
      }}
    </Popover>
  );
};
